'use client';

import React from 'react';

const PAYMENT_OPTIONS = [
    { key: 'visa', label: 'Visa', icon: 'fa-brands fa-cc-visa' },
    {
        key: 'mastercard',
        label: 'Mastercard',
        icon: 'fa-brands fa-cc-mastercard',
    },
    { key: 'amex', label: 'Amex', icon: 'fa-brands fa-cc-amex' },
    { key: 'discover', label: 'Discover', icon: 'fa-brands fa-cc-discover' },
    { key: 'paypal', label: 'PayPal', icon: 'fa-brands fa-cc-paypal' },
    { key: 'applepay', label: 'Apple Pay', icon: 'fa-brands fa-apple-pay' },
    { key: 'googlepay', label: 'Google Pay', icon: 'fa-brands fa-google-pay' },
    { key: 'stripe', label: 'Stripe', icon: 'fa-brands fa-stripe' },
    { key: 'jcb', label: 'JCB', icon: 'fa-brands fa-cc-jcb' },
    {
        key: 'dinersclub',
        label: 'Diners Club',
        icon: 'fa-brands fa-cc-diners-club',
    },
    { key: 'bitcoin', label: 'Bitcoin', icon: 'fa-brands fa-bitcoin' },
    { key: 'cash', label: 'Cash', icon: 'fa-kit fa-cash' },
    { key: 'zelle', label: 'Zelle', icon: 'fa-kit fa-zelle' },
];

const normalize = (value) =>
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

function parseValue(value) {
    if (Array.isArray(value)) {
        return value.map((v) => normalize(v)).filter(Boolean);
    }
    return String(value || '')
        .split(',')
        .map((v) => normalize(v))
        .filter(Boolean);
}

export default function PaymentOptionsEditor({
    value,
    onChange,
    label = 'Payment Methods',
}) {
    const [dragKey, setDragKey] = React.useState(null);
    const [hoverKey, setHoverKey] = React.useState(null);
    const [selected, setSelected] = React.useState(() => parseValue(value));
    const selectedSet = React.useMemo(
        () => new Set(selected.map((v) => normalize(v))),
        [selected]
    );

    React.useEffect(() => {
        const next = parseValue(value);
        if (next.join(',') !== selected.join(',')) {
            setSelected(next);
        }
    }, [value, selected]);

    const emit = (list) => {
        if (!onChange) return;
        onChange(list.join(','));
    };

    const toggle = (key) => {
        if (selectedSet.has(key)) {
            const next = selected.filter((k) => k !== key);
            setSelected(next);
            emit(next);
            return;
        }
        const next = [...selected, key];
        setSelected(next);
        emit(next);
    };

    const onDragStart = (event, key) => {
        event.dataTransfer.setData('text/plain', key);
        event.dataTransfer.effectAllowed = 'move';
        setDragKey(key);
    };

    const onDragOver = (event, key) => {
        event.preventDefault();
        if (
            dragKey &&
            dragKey !== key &&
            selectedSet.has(dragKey) &&
            selectedSet.has(key)
        ) {
            setHoverKey(key);
        }
    };
    const onDragEnter = (event, key) => {
        event.preventDefault();
        if (
            dragKey &&
            dragKey !== key &&
            selectedSet.has(dragKey) &&
            selectedSet.has(key)
        ) {
            setHoverKey(key);
        }
    };

    const commitReorder = () => {
        if (
            dragKey &&
            hoverKey &&
            dragKey !== hoverKey &&
            selectedSet.has(dragKey) &&
            selectedSet.has(hoverKey)
        ) {
            const next = selected.filter((k) => k !== dragKey);
            const idx = next.indexOf(hoverKey);
            if (idx !== -1) {
                next.splice(idx, 0, dragKey);
                setSelected(next);
                emit(next);
            }
        }
        setHoverKey(null);
        setDragKey(null);
    };

    const onDrop = (event) => {
        event.preventDefault();
        commitReorder();
    };

    const onDragEnd = () => {
        commitReorder();
    };

    const displayOptions = React.useMemo(() => {
        const selectedKeys = new Set(selected);
        const orderedSelected = selected
            .map((key) => PAYMENT_OPTIONS.find((opt) => opt.key === key))
            .filter(Boolean);

        // Insert a placeholder where the dragged item would land
        let previewSelected = orderedSelected;
        if (
            dragKey &&
            selectedSet.has(dragKey) &&
            hoverKey &&
            hoverKey !== dragKey &&
            selectedSet.has(hoverKey)
        ) {
            const withoutDrag = orderedSelected.filter(
                (opt) => opt.key !== dragKey
            );
            const idx = withoutDrag.findIndex((opt) => opt.key === hoverKey);
            if (idx !== -1) {
                const dragOpt = PAYMENT_OPTIONS.find(
                    (opt) => opt.key === dragKey
                );
                if (dragOpt) {
                    const withPlaceholder = withoutDrag.slice();
                    withPlaceholder.splice(idx, 0, {
                        ...dragOpt,
                        placeholder: true,
                    });
                    previewSelected = withPlaceholder;
                }
            }
        }

        const remaining = PAYMENT_OPTIONS.filter(
            (opt) => !selectedKeys.has(opt.key)
        );
        return [...previewSelected, ...remaining];
    }, [selected, dragKey, hoverKey, selectedSet]);

    return (
        <div className="mb-2 w-full">
            <div className="mb-2 text-xs font-semibold leading-5 text-primary-700">
                {label}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {displayOptions.map((option) => {
                    const isActive = selectedSet.has(option.key);
                    const draggable = isActive;
                    const isPlaceholder = option.placeholder;
                    return (
                        <button
                            key={option.key}
                            type="button"
                            draggable={draggable}
                            aria-pressed={isActive}
                            aria-label={option.label}
                            title={option.label}
                            onClick={() => toggle(option.key)}
                            onDragStart={(e) => onDragStart(e, option.key)}
                            onDragOver={(e) => onDragOver(e, option.key)}
                            onDragEnter={(e) => onDragEnter(e, option.key)}
                            onDrop={onDrop}
                            onDragEnd={onDragEnd}
                            className={[
                                'inline-flex h-8 items-center justify-center gap-1 rounded-md border bg-white px-2 text-[11px] shadow-sm transition-all duration-75 ease-out',
                                isActive
                                    ? 'border-primary-600 text-primary-600'
                                    : 'border-neutral-300 text-neutral-500 hover:border-primary-400 hover:text-primary-600',
                                isPlaceholder ? 'opacity-40' : '',
                            ].join(' ')}
                        >
                            <i className={option.icon} aria-hidden="true" />
                            <span className="hidden sm:inline">
                                {option.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
